import React, { useMemo, useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, Search, BriefcaseBusiness, CalendarClock, Target } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import RecordPreviewModal from '../components/RecordPreviewModal';
import { useToast } from '../components/ToastProvider';
import { API_BASE_URL } from '../api/client';

const INITIAL_FORM = {
  projectName: '',
  clientName: '',
  description: '',
  budget: '',
  status: 'planning',
  startDate: '',
  endDate: '',
};

const STATUS_META = {
  planning: { label: 'Planning', tone: 'bg-slate-100 text-slate-700 border border-slate-200' },
  'in-progress': { label: 'In Progress', tone: 'bg-blue-100 text-blue-700 border border-blue-200' },
  completed: { label: 'Completed', tone: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  'on-hold': { label: 'On Hold', tone: 'bg-amber-100 text-amber-700 border border-amber-200' },
  cancelled: { label: 'Cancelled', tone: 'bg-rose-100 text-rose-700 border border-rose-200' },
};

const Projects = () => {
  const { showToast } = useToast();
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [previewProject, setPreviewProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);

  useEffect(() => {
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/projects`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      showToast({ title: 'Error', message: 'Unable to load projects', tone: 'error' });
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (isEditing && editingId) {
        await axios.put(`${API_BASE_URL}/projects/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        showToast({ title: 'Updated', message: 'Project updated successfully', tone: 'success' });
      } else {
        await axios.post(`${API_BASE_URL}/projects`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        showToast({ title: 'Created', message: 'Project created successfully', tone: 'success' });
      }

      setShowForm(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      showToast({ title: 'Error', message: 'Failed to save project', tone: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (project) => {
    setFormData({
      projectName: project.projectName || '',
      clientName: project.clientName || '',
      description: project.description || '',
      budget: project.budget || '',
      status: project.status || 'planning',
      startDate: project.startDate ? String(project.startDate).split('T')[0] : '',
      endDate: project.endDate ? String(project.endDate).split('T')[0] : '',
    });
    setEditingId(project.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/projects/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      fetchProjects();
      showToast({ title: 'Deleted', message: 'Project deleted successfully', tone: 'success' });
    } catch (error) {
      console.error('Error deleting project:', error);
      showToast({ title: 'Error', message: 'Failed to delete project', tone: 'error' });
    }
  };

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const matchesSearch =
        project.projectName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [projects, searchQuery, statusFilter]);

  const totalBudget = filteredProjects.reduce((sum, project) => sum + (parseFloat(project.budget) || 0), 0);
  const activeCount = filteredProjects.filter((project) => project.status === 'in-progress').length;
  const completedCount = filteredProjects.filter((project) => project.status === 'completed').length;

  const formatMoney = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(parseFloat(value) || 0);
  };

  return (
    <Layout pageTitle="Projects Command Center" pageSubtitle="Plan work, track budgets, and monitor delivery momentum">
      <div className="app-page-shell space-y-5">
        <section className="app-page-hero">
          <p className="app-page-eyebrow">Portfolio Overview</p>
          <h2>Keep every engagement on budget and on timeline.</h2>
          <p>Track project health in one place, quickly open records for review, and execute edits without losing context.</p>
        </section>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">Total Projects</p>
              <BriefcaseBusiness size={18} className="text-slate-400" />
            </div>
            <p className="mt-2 text-2xl font-bold text-slate-900">{filteredProjects.length}</p>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm text-blue-600">In Progress</p>
              <CalendarClock size={18} className="text-blue-500" />
            </div>
            <p className="mt-2 text-2xl font-bold text-blue-700">{activeCount}</p>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm text-emerald-700">Completed</p>
              <Target size={18} className="text-emerald-600" />
            </div>
            <p className="mt-2 text-2xl font-bold text-emerald-700">{completedCount}</p>
          </div>
          <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-5 shadow-sm">
            <p className="text-sm text-indigo-700">Portfolio Budget</p>
            <p className="mt-2 text-2xl font-bold text-indigo-700">{formatMoney(totalBudget)}</p>
          </div>
        </section>

        <section className="app-surface space-y-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-80">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by project, client, or description"
                  className="w-full border border-slate-300 bg-white py-2.5 pl-9 pr-3"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-slate-300 bg-white px-3 py-2.5 sm:w-44"
              >
                <option value="all">All Statuses</option>
                <option value="planning">Planning</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="on-hold">On Hold</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <button
              onClick={() => {
                if (showForm) {
                  setShowForm(false);
                  resetForm();
                } else {
                  setShowForm(true);
                }
              }}
              className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5"
            >
              <Plus size={18} />
              <span>{showForm ? 'Close Form' : 'New Project'}</span>
            </button>
          </div>

          {showForm && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">{isEditing ? 'Edit Project' : 'Create New Project'}</h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="app-icon-btn"
                  aria-label="Close project form"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <input
                    type="text"
                    name="projectName"
                    placeholder="Project Name"
                    value={formData.projectName}
                    onChange={handleChange}
                    required
                    className="border border-slate-300 bg-white px-4 py-2.5"
                  />
                  <input
                    type="text"
                    name="clientName"
                    placeholder="Client Name"
                    value={formData.clientName}
                    onChange={handleChange}
                    required
                    className="border border-slate-300 bg-white px-4 py-2.5"
                  />
                </div>

                <textarea
                  name="description"
                  placeholder="Scope, milestones, and deliverables"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full border border-slate-300 bg-white px-4 py-2.5"
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <input
                    type="number"
                    name="budget"
                    placeholder="Budget"
                    value={formData.budget}
                    onChange={handleChange}
                    step="0.01"
                    required
                    className="border border-slate-300 bg-white px-4 py-2.5"
                  />
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="border border-slate-300 bg-white px-4 py-2.5"
                  />
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="border border-slate-300 bg-white px-4 py-2.5"
                  />
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="border border-slate-300 bg-white px-4 py-2.5"
                  >
                    <option value="planning">Planning</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on-hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary px-6 py-2.5"
                  >
                    {isSubmitting ? 'Saving...' : isEditing ? 'Update Project' : 'Create Project'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      resetForm();
                    }}
                    className="btn-secondary px-6 py-2.5"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {filteredProjects.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="text-base font-semibold text-slate-700">No projects found</p>
                <p className="mt-1 text-sm text-slate-500">Try changing your filters or create a new project.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-600">Project</th>
                      <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-600">Client</th>
                      <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-600">Budget</th>
                      <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-600">Status</th>
                      <th className="px-5 py-3 text-left text-xs font-bold uppercase tracking-[0.1em] text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredProjects.map((proj) => {
                      const statusMeta = STATUS_META[proj.status] || STATUS_META.planning;
                      return (
                        <tr
                          key={proj.id}
                          className="cursor-pointer transition-colors hover:bg-slate-50"
                          onClick={() => setPreviewProject(proj)}
                        >
                          <td className="px-5 py-4">
                            <p className="text-sm font-semibold text-slate-900">{proj.projectName}</p>
                            <p className="mt-1 max-w-xs truncate text-xs text-slate-500">{proj.description || 'No description provided'}</p>
                          </td>
                          <td className="px-5 py-4 text-sm text-slate-700">{proj.clientName}</td>
                          <td className="px-5 py-4 text-sm font-semibold text-slate-900">{formatMoney(proj.budget)}</td>
                          <td className="px-5 py-4 text-sm">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusMeta.tone}`}>
                              {statusMeta.label}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewProject(proj);
                                }}
                                className="app-icon-btn text-blue-600 hover:text-blue-800"
                                title="Preview"
                              >
                                <Eye size={17} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(proj);
                                }}
                                className="app-icon-btn text-amber-600 hover:text-amber-800"
                                title="Edit"
                              >
                                <Edit2 size={17} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(proj.id);
                                }}
                                className="app-icon-btn text-rose-600 hover:text-rose-800"
                                title="Delete"
                              >
                                <Trash2 size={17} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {previewProject && (
          <RecordPreviewModal
            title={previewProject.projectName || `Project #${previewProject.id}`}
            subtitle="Project Record"
            onClose={() => setPreviewProject(null)}
            fields={[
              { label: 'Client', value: previewProject.clientName },
              { label: 'Status', value: STATUS_META[previewProject.status]?.label || previewProject.status },
              { label: 'Budget', value: formatMoney(previewProject.budget || 0) },
              { label: 'Start Date', value: previewProject.startDate ? new Date(previewProject.startDate).toLocaleDateString() : 'N/A' },
              { label: 'End Date', value: previewProject.endDate ? new Date(previewProject.endDate).toLocaleDateString() : 'N/A' },
              { label: 'Description', value: previewProject.description || 'No description provided' },
            ]}
          />
        )}
      </div>
    </Layout>
  );
};

export default Projects;